import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { InternalGetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { ExternalLink } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { ModalTrigger } from '~/common/components/Modal/ModalTrigger';
import { ClassificationMethodBadge } from './ClassificationMethodBadge';
import { ClassificationOverrideModal } from './ClassificationOverrideModal';
import { ClassificationScoreBadge } from './ClassificationScoreBadge';
import { DocumentClassification } from './classification.types';

interface ClassificationCompactDisplayProps {
  classification: Array<DocumentClassification>;
  ingestionDocument?: InternalGetIngestionDocumentFragment;
}

// Helper function to find matching contract document for a classification candidate
const findMatchingContractDocument = (
  candidate: DocumentClassification,
  contractDocs?: InternalGetIngestionDocumentFragment['contractDocuments']
) => {
  if (!contractDocs || contractDocs.length === 0) return null;

  // Try to match by form name and version
  return contractDocs.find((contractDoc) => {
    const doc = contractDoc.documentVersion?.document;
    const version = contractDoc.documentVersion;
    if (!doc) return false;

    // Match by form name
    const formMatches = doc.form === candidate.formName;
    // If version is specified in candidate, also match by version
    const versionMatches =
      !candidate.version || version?.version === candidate.version;

    return formMatches && versionMatches;
  });
};

export const ClassificationCompactDisplay: FC<
  ClassificationCompactDisplayProps
> = ({ classification, ingestionDocument }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedAlternatives, setExpandedAlternatives] = useState<Set<number>>(
    new Set()
  );

  const toggleAlternatives = (pageIndex: number) => {
    const newExpanded = new Set(expandedAlternatives);
    if (newExpanded.has(pageIndex)) {
      newExpanded.delete(pageIndex);
    } else {
      newExpanded.add(pageIndex);
    }
    setExpandedAlternatives(newExpanded);
  };

  if (!classification || classification.length === 0) {
    return (
      <Box p={3}>
        <Text color="compliance.text.muted" fontSize="sm" fontWeight="medium">
          No classification data available
        </Text>
      </Box>
    );
  }

  const identifiedPages = classification.filter(
    (p) => p.formName !== 'UNKNOWN'
  );
  const highConfidencePages = classification.filter((p) => p.score >= 0.94);
  const avgScore =
    classification.reduce((sum: number, p) => sum + p.score, 0) /
    classification.length;
  const avgContainment =
    classification.reduce((sum: number, p) => sum + (p.containment || 0), 0) /
    classification.length;

  const displayedPages = isExpanded
    ? classification
    : classification.slice(0, 5);
  const hasMorePages = classification.length > 5;
  const extractionValidationMessage =
    ingestionDocument?.isValidDocumentMessageForExtraction?.trim() || null;

  return (
    <VStack align="stretch" spacing={3}>
      {extractionValidationMessage && (
        <Alert
          alignItems="flex-start"
          borderRadius="md"
          status="warning"
          variant="subtle"
        >
          <AlertIcon boxSize={5} mt={1} />
          <VStack align="stretch" spacing={1}>
            <AlertTitle fontSize="sm" fontWeight="semibold">
              Unable to classify this document
            </AlertTitle>
            <AlertDescription fontSize="sm">
              {extractionValidationMessage}
            </AlertDescription>
          </VStack>
        </Alert>
      )}
      {ingestionDocument && (
        <ClassificationOverrideBanner
          ingestionDocumentId={ingestionDocument.id}
        />
      )}
      <HStack align="flex-start" justify="space-between">
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            Pages: {classification.length}
          </Text>
          <Text color="compliance.text.secondary" fontSize="xs">
            {identifiedPages.length} identified, {highConfidencePages.length}{' '}
            high confidence
          </Text>
        </VStack>
        <VStack align="end" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            Avg Score:
          </Text>
          <ClassificationScoreBadge
            precision={2}
            score={avgScore}
            size="sm"
            type="similarity"
          />
          <ClassificationScoreBadge
            precision={2}
            score={avgContainment}
            size="sm"
            type="containment"
          />
        </VStack>
      </HStack>

      <Box>
        <Text fontSize="xs" fontWeight="medium" mb={2}>
          Page Classifications:
        </Text>
        <VStack align="stretch" spacing={2}>
          {displayedPages.map((pred, index: number) => {
            const matchingDoc = findMatchingContractDocument(
              pred,
              ingestionDocument?.contractDocuments
            );

            return (
              <VStack key={index} align="stretch" spacing={1}>
                <HStack
                  align="start"
                  borderRadius="md"
                  cursor="pointer"
                  mx={-1}
                  p={1}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleAlternatives(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleAlternatives(index);
                    }
                  }}
                >
                  <ClassificationCandidateDetails
                    candidate={pred}
                    classificationMethod={pred.classificationMethod}
                    contractDocument={matchingDoc}
                    documentIndex={index + 1}
                    pageNumber={pred.pageNumber}
                    scoreBadgeSize="xs"
                    scorePrecision={4}
                    variant="primary"
                  />
                  {pred.alternativeCandidates &&
                    pred.alternativeCandidates.length > 0 && (
                      <ChevronRightIcon
                        color="gray.400"
                        transform={
                          expandedAlternatives.has(index)
                            ? 'rotate(90deg)'
                            : 'rotate(0deg)'
                        }
                        transition="transform 0.2s"
                      />
                    )}
                </HStack>

                {pred.alternativeCandidates &&
                  pred.alternativeCandidates.length > 0 && (
                    <Collapse in={expandedAlternatives.has(index)}>
                      <Box
                        bg="compliance.debug.code.bg"
                        borderRadius="md"
                        ml={2}
                        mt={2}
                        p={3}
                      >
                        <Text
                          color="compliance.debug.text"
                          fontSize="xs"
                          fontWeight="medium"
                          mb={2}
                        >
                          Alternative Classifications:
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {pred.alternativeCandidates.map((alt, altIndex) => (
                            <HStack
                              key={altIndex}
                              bg="compliance.card.bg"
                              borderRadius="sm"
                              justify="space-between"
                              p={2}
                            >
                              <ClassificationCandidateDetails
                                candidate={alt}
                                documentIndex={index + 1}
                                scoreBadgeSize="sm"
                                scorePrecision={5}
                                variant="alternative"
                              />
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </Collapse>
                  )}
              </VStack>
            );
          })}
        </VStack>

        {hasMorePages && (
          <Button
            _hover={{ bg: 'transparent' }}
            mt={2}
            px={0}
            size="xs"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded
              ? `Show Less`
              : `Show All ${classification.length} Pages`}
          </Button>
        )}
      </Box>

      {classification.some((p) => p.classificationMethod) && (
        <Box>
          <Text fontSize="xs" fontWeight="medium" mb={1}>
            Classification Methods:
          </Text>
          <Wrap spacing={1}>
            {Array.from(
              new Set(
                classification
                  .map((p) => p.classificationMethod)
                  .filter((method): method is string => Boolean(method))
              )
            ).map((method, index: number) => (
              <WrapItem key={index}>
                <ClassificationMethodBadge method={method} />
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {ingestionDocument && (
        <ModalTrigger
          modal={ClassificationOverrideModal}
          modalProps={{
            classification,
            ingestionDocument,
          }}
        >
          <Button size="sm" variant="outline" w="full">
            Override Classification
          </Button>
        </ModalTrigger>
      )}
    </VStack>
  );
};

const ClassificationOverrideBanner: FC<{
  ingestionDocumentId: string;
}> = ({ ingestionDocumentId }) => {
  const [overrideSubmitted, setOverrideSubmitted] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const evt = e as CustomEvent<{ ingestionDocumentId?: string }>;
      if (evt.detail?.ingestionDocumentId === ingestionDocumentId) {
        setOverrideSubmitted(true);
      }
    };

    window.addEventListener('classificationOverrideSubmitted', handler);

    return () => {
      window.removeEventListener('classificationOverrideSubmitted', handler);
    };
  }, [ingestionDocumentId]);

  if (!overrideSubmitted) return null;

  return (
    <Alert borderRadius="md" status="info">
      <AlertIcon />
      <VStack align="start" spacing={0}>
        <AlertTitle fontSize="sm" fontWeight="medium">
          Classification override submitted
        </AlertTitle>
        <AlertDescription fontSize="xs">
          Classification will be renewed after re-ingestion.
        </AlertDescription>
      </VStack>
    </Alert>
  );
};

type AlternativeCandidate = NonNullable<
  DocumentClassification['alternativeCandidates']
>[number];

interface ClassificationCandidateDetailsProps {
  documentIndex: number;
  pageNumber?: number;
  classificationMethod?: string;
  scorePrecision: number;
  scoreBadgeSize: 'xs' | 'sm';
  contractDocument?:
    | NonNullable<InternalGetIngestionDocumentFragment['contractDocuments']>[0]
    | null;
}

interface PrimaryClassificationCandidateDetailsProps
  extends ClassificationCandidateDetailsProps {
  variant: 'primary';
  candidate: DocumentClassification;
}

interface AlternativeClassificationCandidateDetailsProps
  extends ClassificationCandidateDetailsProps {
  variant: 'alternative';
  candidate: AlternativeCandidate;
}

const ClassificationCandidateDetails: FC<
  | PrimaryClassificationCandidateDetailsProps
  | AlternativeClassificationCandidateDetailsProps
> = ({
  documentIndex,
  candidate,
  variant,
  pageNumber,
  classificationMethod,
  scorePrecision,
  scoreBadgeSize,
  contractDocument,
}) => {
  const labelColor =
    variant === 'primary'
      ? 'compliance.text.muted'
      : 'compliance.text.secondary';

  const documentId = contractDocument?.documentVersion?.document?.id;
  const versionId = contractDocument?.documentVersion?.id;

  return (
    <VStack align="start" flex={1} spacing={0}>
      <HStack>
        <Text fontSize="xs" fontWeight="medium">
          {variant === 'primary' && typeof pageNumber === 'number'
            ? `Page ${documentIndex}: ${candidate.formName}`
            : candidate.formName}
        </Text>
        {documentId && (
          <Link
            isExternal
            _hover={{ opacity: 0.7 }}
            aria-label="Open document in a new tab"
            href={`/internal/documents/${documentId}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Icon as={ExternalLink} boxSize={4} color="compliance.text.muted" />
          </Link>
        )}
        <ClassificationScoreBadge
          precision={scorePrecision}
          score={candidate.score}
          size={scoreBadgeSize}
          type="similarity"
        />
        {typeof candidate.containment === 'number' && (
          <ClassificationScoreBadge
            precision={2}
            score={candidate.containment}
            size={scoreBadgeSize}
            type="containment"
          />
        )}
      </HStack>

      {variant === 'primary' && classificationMethod && (
        <HStack color="compliance.text.muted" fontSize="xs" mt={1} spacing={1}>
          <Text>Method:</Text>
          <ClassificationMethodBadge method={classificationMethod} size="xs" />
        </HStack>
      )}

      {variant === 'primary' &&
        (candidate.alternativeCandidates ?? []).length > 4 && (
          <Text color="compliance.text.muted" fontSize="xs">
            (Prioritized by expected page count match)
          </Text>
        )}

      {candidate.version && (
        <HStack spacing={1}>
          <ClassificationCandidateMeta
            label="Version"
            labelColor={labelColor}
            value={candidate.version}
          />
          {documentId && versionId && (
            <Link
              isExternal
              _hover={{ opacity: 0.7 }}
              aria-label="Open document version in a new tab"
              href={`/internal/documents/${documentId}/${versionId}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Icon
                as={ExternalLink}
                boxSize="14px"
                color="compliance.text.muted"
              />
            </Link>
          )}
        </HStack>
      )}
      {candidate.documentCategory && (
        <ClassificationCandidateMeta
          label="Category"
          labelColor={labelColor}
          value={candidate.documentCategory}
        />
      )}
      {candidate.documentType && (
        <ClassificationCandidateMeta
          label="Type"
          labelColor={labelColor}
          value={candidate.documentType}
        />
      )}
      {candidate.searchRegex && (
        <ClassificationCandidateMeta
          label="Search Regex"
          labelColor={labelColor}
          value={candidate.searchRegex}
        />
      )}
      {candidate.usState && (
        <ClassificationCandidateMeta
          label="State"
          labelColor={labelColor}
          value={candidate.usState}
        />
      )}
      {candidate.pageNumber && (
        <ClassificationCandidateMeta
          label="Page"
          labelColor={
            candidate.pageNumber === documentIndex
              ? labelColor
              : 'compliance.status.error'
          }
          textColor={
            candidate.pageNumber === documentIndex
              ? undefined
              : 'compliance.status.error'
          }
          value={candidate.pageNumber}
        />
      )}
    </VStack>
  );
};

const ClassificationCandidateMeta: FC<{
  label: string;
  value: string | number;
  labelColor: string;
  textColor?: string;
}> = ({ label, value, labelColor, textColor }) => (
  <Text color={labelColor} fontSize="xs">
    {label}:
    <Text as="span" color={textColor || 'compliance.text.secondary'} ml={1}>
      {value}
    </Text>
  </Text>
);
